import React, { useEffect, useState } from "react";
import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Table,
  TablePagination,
  Modal,
  Box,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Grid,
  Button,
} from "@mui/material";
import {
  CommanTableContain,
  PaginationContain,
  DialContainer,
  StylingDialog,
  ModalContain,
  ActionModal,
} from "../../style/Admin/SuggestionStatus";
import {
  getAllSuggestions,
  getSuggestionDetails,
  deleteSuggestionDetails,
} from "../../services/suggestionServices";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DashboardHeader from "../../components/Common/DashboardHeader";

const SuggestionsList = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalData, setTotalData] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const handleOpenDialog = async (suggestionId) => {
    try {
      const response = await getSuggestionDetails(suggestionId);
      if (response.success) {
        setSelectedSuggestion({
          users: response.data.users.map((user) => ({
            id: user.userId,
            name: user.userName,
          })),
          courses: response.data.courses.map((course) => ({
            id: course.courseNumber,
            name: course.courseName,
          })),
        });
        setDialogOpen(true);
      } else {
        toast.error("Failed to fetch suggestion details");
      }
    } catch (error) {
      toast.error("Error loading suggestion details");
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
   };

  const handleDeleteClick = (suggestionId) => {
    setDeleteId(suggestionId);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteSuggestionDetails(deleteId);
      if (response.success) {
        toast.success("Suggestion deleted successfully!");
        fetchSuggestions(); // Refresh the list
      } else {
        toast.error("Failed to delete suggestion");
      }
    } catch (error) {
      toast.error("Error deleting suggestion");
    } finally {
      setDeleteModalOpen(false);
      setDeleteId(null);
    }
  };

  const headers = [
    { id: "id", label: "Suggestion Id" },
    { id: "date", label: "Date" },
    { id: "userCount", label: "User Count" },
    { id: "courseCount", label: "Course Count" },
    { id: "showStats", label: "Show Stats" },
    { id: "delete", label: "Delete" },
  ];

  const fetchSuggestions = async () => {
    try {
      const payload = {
        page,
        offset: rowsPerPage,
      };

      const response = await getAllSuggestions(payload);

      if (response.success) {
        setSuggestions(response.stats || []);
        setTotalData(response.totalCount || 0);

        if (response.stats.length === 0) {
          toast.info("No suggestions found.", { toastId: "no-suggestions" });
        }
      } else {
        setSuggestions([]);
        setTotalData(0);
        toast.info("No suggestions found.", { toastId: "no-suggestions" });
      }
    } catch (error) {
      setSuggestions([]);
      setTotalData(0);
      toast.error("Error fetching suggestions");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    fetchSuggestions();
  }, [rowsPerPage, page]);

  return (
    <>
      <DashboardHeader title="Suggestion Status" />
      <CommanTableContain>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>S. No.</TableCell>
              {headers.map((header) => (
                <TableCell key={header.id}>{header.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {suggestions.map((item, rowIndex) => (
              <TableRow key={rowIndex}>
                <TableCell>{rowsPerPage * page + rowIndex + 1}</TableCell>
                <TableCell>{item.suggestionId}</TableCell>
                <TableCell>{item.date}</TableCell>
                <TableCell>{item.userCount}</TableCell>
                <TableCell>{item.courseCount}</TableCell>
                <TableCell>
                  <InfoOutlinedIcon
                    onClick={() => handleOpenDialog(item.suggestionId)}
                    sx={{ cursor: "pointer", color: "primary.main" }}
                  />
                </TableCell>
                <TableCell>
                  <DeleteIcon
                    className="iconDelete"
                    sx={{ cursor: "pointer", color: "error.main" }}
                    onClick={() => handleDeleteClick(item.suggestionId)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CommanTableContain>

      <PaginationContain>
        <TablePagination
          component="div"
          count={totalData}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </PaginationContain>
      <StylingDialog
  open={dialogOpen}
  onClose={handleCloseDialog}
  fullWidth
  maxWidth="md"
  className="suggestion-details-dialog"
>
  <DialContainer>
    <DialogTitle>Suggestion Details</DialogTitle>
  </DialContainer>

  <DialogContent>
    {selectedSuggestion && (
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <CommanTableContain>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User Id</TableCell>
                    <TableCell>User Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedSuggestion.users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.id}</TableCell>
                      <TableCell>{user.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CommanTableContain>
          </Grid>
          <Grid item xs={6}>
            <CommanTableContain>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Course No.</TableCell>
                    <TableCell>Course Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedSuggestion.courses.map((course) => (
                    <TableRow key={course.id}>
                      <TableCell>{course.id}</TableCell>
                      <TableCell>{course.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CommanTableContain>
          </Grid>
        </Grid>
      </Box>
    )}
  </DialogContent>

  <DialogActions>
    {/* Button styled via .custom-button */}
    <button className="custom-button" onClick={handleCloseDialog}>
      Close
    </button>
 
     
  </DialogActions>
</StylingDialog>


      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <ModalContain>
          <ActionModal>
            <div className="IconContain">
              <DeleteForeverIcon />
            </div>
            <h4 className="Title">Are you sure you want to delete this?</h4>
            <p>This action can't be undone!</p>
            <div className="ButtonContain">
              <Button 
                variant="contained" 
                className="Delete" 
                onClick={handleDeleteConfirm}
              >
                Delete
              </Button>
              <Button 
                variant="contained" 
                className="Cancel" 
                onClick={() => setDeleteModalOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </ActionModal>
        </ModalContain>
      </Dialog>

      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default SuggestionsList;
